import { useMutation, useQueryClient } from '@tanstack/react-query';
import { optInFeaturesQueryKeys } from '../Constants/queryKeys';
import { restoreOptInFeature } from '../Services/OptInFeatures.service';
import type { OptInFeature, OptInFeatureListResponse } from '../Types/OptInFeatures.types';
import { useActionToast } from '@/Hooks/useActionToast';

/**
 * Undoes the soft delete of a feature — `PATCH /:id/restore`, behind `opt_in_feature:update`.
 *
 * The `200` carries the whole feature, so the row is **put back from the response** instead of
 * refetched: every cached listing that already holds that id gets the restored object written over
 * it. A refetch would work too and would be one more round trip between clicking "Restaurar" and
 * seeing the row stop being grey.
 *
 * Listings that do **not** hold the id are left alone on purpose — a listing without
 * `includeDeleted` never showed the row, and inventing it there would put a feature in a list that
 * did not ask for it.
 *
 * **`isActive` comes back as it was**, which is why the toast can promise the choices are intact:
 * restoring gives the toggle back and gives each person the state they had, dates and all. Nothing
 * is delivered by this call.
 */
export const useRestoreOptInFeature = () => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  return useMutation({
    mutationFn: (id: string) => restoreOptInFeature(id),
    onSuccess: restored => {
      queryClient.setQueriesData<OptInFeatureListResponse>(
        { queryKey: optInFeaturesQueryKeys.lists() },
        current =>
          current && current.data.some(feature => feature.id === restored.id)
            ? {
                ...current,
                data: current.data.map(feature =>
                  feature.id === restored.id ? restored : feature,
                ),
              }
            : current,
      );
      queryClient.setQueryData<OptInFeature>(optInFeaturesQueryKeys.detail(restored.id), restored);

      toast.success(
        `"${restored.name}" restaurada`,
        restored.isActive
          ? 'Voltou ativa, como estava. Quem já tinha ligado continua com a escolha e as datas intactas.'
          : 'Voltou desativada, como estava antes da exclusão. O estado de cada pessoa continua guardado.',
      );
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível restaurar a funcionalidade', error);
    },
  });
};
